import React, { useState, useEffect } from 'react';

function StudentOnboarding({ studentId, onComplete }) {
    // 1. SECURITY
    const [newPassword, setNewPassword] = useState('');
    
    // 2. LOCATIONS
    const [pickupAddress, setPickupAddress] = useState('');
    const [pickupPostcode, setPickupPostcode] = useState('');
    const [pickupType, setPickupType] = useState('home');
    
    // --- SECONDARY LOCATION STATE ---
    const [showSecondary, setShowSecondary] = useState(false);
    const [secondaryAddress, setSecondaryAddress] = useState('');
    const [secondaryPostcode, setSecondaryPostcode] = useState('');
    const [secondaryType, setSecondaryType] = useState('work');

    // 3. CONTACTS
    const [phoneNumber, setPhoneNumber] = useState('');
    const [emergencyName, setEmergencyName] = useState('');
    const [emergencyNumber, setEmergencyNumber] = useState('');

    // 4. DRIVING DATA & PERSONAL INFO
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [experienceHours, setExperienceHours] = useState('0');
    const [licenseNumber, setLicenseNumber] = useState('');
    const [theoryDate, setTheoryDate] = useState('');
    const [practicalDate, setPracticalDate] = useState('');

    // Pure string date stripper: completely avoids local timezone rollbacks
    const formatDateForInput = (dateVal) => {
        if (!dateVal) return '';
        const str = String(dateVal).trim();
        return str.length >= 10 ? str.slice(0, 10) : '';
    };

    // --- PRE-FILL DATA FROM DATABASE ---
    useEffect(() => {
        const fetchExistingData = async () => {
            try {
                const res = await fetch(`/api/learners/${studentId}`);
                if (res.ok) {
                    const data = await res.json();
                    console.log("Database returned this object:", data);

                    // 1. Map Phone Number
                    setPhoneNumber(data.phone_number || '');

                    // 2. Map Emergency Contact (Split "Name: Number")
                    if (data.emergency_contact) {
                        const parts = data.emergency_contact.split(': ');
                        setEmergencyName(parts[0] || '');
                        setEmergencyNumber(parts[1] || '');
                    }

                    // 3. Map Addresses
                    if (data.default_pickup_address) {
                        const [addr, post] = data.default_pickup_address.split(', ');
                        setPickupAddress(addr || '');
                        setPickupPostcode(post || '');
                    }
                    setPickupType(data.default_pickup_type || 'home');

                    // 4. Map Secondary
                    if (data.secondary_pickup) {
                        setShowSecondary(true);
                        const [addr, post] = data.secondary_pickup.split(', ');
                        setSecondaryAddress(addr || '');
                        setSecondaryPostcode(post || '');
                        setSecondaryType(data.secondary_type || 'work');
                    }

                    // 5. Driving Data & DOB
                    setDateOfBirth(formatDateForInput(data.date_of_birth));
                    setExperienceHours(data.prior_hours_experience?.toString() || '0');
                    setLicenseNumber(data.license_number || '');
                    setTheoryDate(formatDateForInput(data.theory_passed_date));
                    setPracticalDate(formatDateForInput(data.practical_test_date));
                }
            } catch (err) { 
                console.error("Error loading profile:", err); 
            }
        };
        fetchExistingData();
    }, [studentId]);

    // --- SUBMISSION LOGIC ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`/api/learners/${studentId}/onboarding`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    newPassword: newPassword || undefined,
                    phone_number: phoneNumber,
                    emergency_contact: `${emergencyName}: ${emergencyNumber}`,
                    date_of_birth: dateOfBirth,
                    prior_hours_experience: experienceHours,
                    license_number: licenseNumber,
                    theory_passed_date: theoryDate,
                    practical_test_date: practicalDate,
                    default_pickup_address: `${pickupAddress}, ${pickupPostcode}`,
                    default_pickup_type: pickupType,
                    secondary_pickup: showSecondary ? `${secondaryAddress}, ${secondaryPostcode}` : null,
                    secondary_type: showSecondary ? secondaryType : null
                })
            });

            if (response.ok) {
                // Clear the force onboarding flag once completed
                localStorage.removeItem('forceOnboarding');
                
                alert("🚀 Profile updated successfully!");
                onComplete();
            }
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 3000,
            display: 'flex',
            justifyContent: 'flex-end' // 👈 Pins the drawer to the right side
        }} onClick={onComplete}>
            <div 
                style={{
                    background: '#ffffff', 
                    width: '100%', 
                    maxWidth: '460px',            // 👈 Matches instructor drawer width
                    height: '100vh',              // 👈 Full screen height
                    position: 'relative', 
                    boxSizing: 'border-box', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '20px', 
                    overflowY: 'auto',            // 👈 Smooth vertical scrolling
                    padding: '20px', 
                    borderLeft: '1px solid #cbd5e0', 
                    color: '#111111',
                    fontFamily: 'sans-serif',
                    textAlign: 'left',
                    boxShadow: '-5px 0 25px rgba(0,0,0,0.1)'
                }} 
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #e2e8f0', paddingBottom: '15px' }}>
                    <h2 style={{ margin: 0, color: '#0066cc', fontWeight: '800', fontSize: '18px' }}>⚙️ Student Account Settings</h2>
                    <button onClick={onComplete} style={{ background: 'none', border: 'none', color: '#111111', fontSize: '22px', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    
                    {/* 1. PASSWORD */}
                    <div>
                        <label style={labelStyle}>🔒 New Password (Leave blank to keep current)</label>
                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inputStyle} />
                    </div>

                    {/* 2. LOCATIONS */}
                    <div>
                        <label style={labelStyle}>📍 Primary Pickup Address</label>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                            <input required value={pickupAddress} onChange={e => setPickupAddress(e.target.value)} style={{...inputStyle, flex: 2}} placeholder="Address" />
                            <input required value={pickupPostcode} onChange={e => setPickupPostcode(e.target.value)} style={{...inputStyle, flex: 1}} placeholder="Postcode" />
                        </div>
                        <select value={pickupType} onChange={e => setPickupType(e.target.value)} style={inputStyle}>
                            <option value="home">🏠 Home</option>
                            <option value="work">💼 Work</option>
                            <option value="uni">🎓 Uni</option>
                        </select>

                        <button type="button" onClick={() => setShowSecondary(!showSecondary)} style={{ background: 'transparent', border: '1px dashed #cbd5e0', color: '#0066cc', padding: '10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', marginTop: '10px', fontWeight: 'bold', width: '100%' }}>
                            {showSecondary ? '— Remove secondary location' : '+ Add/Edit secondary pickup location'}
                        </button>

                        {showSecondary && (
                            <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #cbd5e0', marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <label style={labelStyle}>🏢 Secondary Location</label>
                                <input value={secondaryAddress} onChange={e => setSecondaryAddress(e.target.value)} style={inputStyle} placeholder="Address" />
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <input value={secondaryPostcode} onChange={e => setSecondaryPostcode(e.target.value)} style={{...inputStyle, flex: 1}} placeholder="Postcode" />
                                    <select value={secondaryType} onChange={e => setSecondaryType(e.target.value)} style={{...inputStyle, flex: 1}}>
                                        <option value="work">💼 Work</option>
                                        <option value="uni">🎓 Uni</option>
                                        <option value="other">📦 Other</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 3. CONTACTS */}
                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '15px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                            <label style={labelStyle}>📱 Telephone Number</label>
                            <input type="tel" required value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>🆘 Emergency Contact</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <input required value={emergencyName} onChange={e => setEmergencyName(e.target.value)} style={{...inputStyle, flex: 1}} placeholder="Name" />
                                <input required value={emergencyNumber} onChange={e => setEmergencyNumber(e.target.value)} style={{...inputStyle, flex: 1}} placeholder="Number" />
                            </div>
                        </div>
                    </div>

                    {/* 4. DRIVING DATA & PERSONAL INFO */}
                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '15px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                            <label style={labelStyle}>🎂 Date of Birth</label>
                            <input type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} style={inputStyle} />
                        </div>

                        <div>
                            <label style={labelStyle}>🆔 DVLA Driving License Number</label>
                            <input type="text" value={licenseNumber} onChange={e => setLicenseNumber(e.target.value)} style={inputStyle} placeholder="Enter license number..." />
                        </div>

                        <div>
                            <label style={labelStyle}>⏱️ Driving Hours Done</label>
                            <input type="number" value={experienceHours} onChange={e => setExperienceHours(e.target.value)} style={inputStyle} />
                        </div>
                        
                        <div>
                            <label style={labelStyle}>📝 Theory Pass Date</label>
                            <input type="date" value={theoryDate} onChange={e => setTheoryDate(e.target.value)} style={inputStyle} />
                        </div>
                        
                        <div>
                            <label style={labelStyle}>🏁 Practical Test Date</label>
                            <input type="date" value={practicalDate} onChange={e => setPracticalDate(e.target.value)} style={inputStyle} />
                        </div>
                    </div>

                    <button type="submit" style={{ padding: '15px', background: '#2f855a', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px', marginTop: '10px', marginBottom: '20px' }}>
                        Save Profile Settings 💾
                    </button>
                </form>
            </div>
        </div>
    );
}

const labelStyle = { display: 'block', fontSize: '11px', color: '#4a5568', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '5px' };
const inputStyle = { width: '100%', padding: '10px', background: '#ffffff', border: '1px solid #cbd5e0', borderRadius: '6px', color: '#111111', boxSizing: 'border-box', fontWeight: '500' };

export default StudentOnboarding;